/**
 * Event detail (/events/:eventId) — FEA-22 member view, FEA-23 staff
 * management, docs/FRONTEND-PLAN.md §2.5.
 *
 * `GET /events/{eventId}` 404s for drafts, cancelled events, and (by design)
 * accreditedOnly events the caller can't see — there's no 403 on GET, so a
 * 404 here is rendered as a plain "not found", never permission language.
 * Staff bypass those checks server-side, so this page also doubles as the
 * detail view for draft/cancelled/completed events when isEventStaff.
 * Registration state comes from the same `useMyRegistrations` join Events.jsx
 * uses, so switching pages never shows contradictory state.
 *
 * The staff-only Manage section (roster, door check-in, badge printing) is
 * rendered inline rather than as a separate route — one page, one data load,
 * and staff is a small enough audience that a second route buys nothing.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as eventsApi from '../api/events';
import { ApiError } from '../lib/apiClient';
import { usePagedList } from '../lib/usePagedList';
import { useMyRegistrations } from '../lib/useMyRegistrations';
import { useConfirm } from '../lib/confirm';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/toast';
import { openHtmlDocument, buildBadgeBatchDocument } from '../lib/printWindow';
import CheckInCode from '../components/CheckInCode';
import Modal from '../components/Modal';
import EventForm, { eventToForm } from '../components/EventForm';
import {
  ErrorAlert,
  Field,
  TextareaField,
  Spinner,
  EmptyState,
  PagedListFooter,
  RelativeTime,
  StatusPill,
  UserChip,
  formatDateTime,
} from '../components/ui';
import { cn, ui } from '../lib/ui';
import { acceptsAttendeeList, MAX_ATTENDANCE_EMAILS, parseAttendeeEmails } from '../lib/attendance';

const ROSTER_PAGE_SIZE = 50;

function openBadgeOrWarn(html, toast, options) {
  const win = openHtmlDocument(html, options);
  if (!win) {
    toast.error(
      'Your browser blocked the badge window. Allow pop-ups for this site and try again.'
    );
  }
  return win;
}

export default function EventDetail() {
  const { eventId } = useParams();
  // Staff for events = employee/sales/admin (src/services/events/helpers.js
  // STAFF_ROLES), which matches auth's isStaff (FEA-19).
  const { isStaff: isEventStaff } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const registrations = useMyRegistrations();

  const [state, setState] = useState({ loading: true, error: null, notFound: false, event: null });
  const [busy, setBusy] = useState(false);
  const [badgeBusy, setBadgeBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const load = useCallback(() => {
    setState(current => ({ ...current, loading: true, error: null, notFound: false }));
    eventsApi
      .getEvent(eventId)
      .then(d => setState({ loading: false, error: null, notFound: false, event: d.event }))
      .catch(err => {
        const notFound = err instanceof ApiError && err.statusCode === 404;
        setState({ loading: false, error: notFound ? null : err, notFound, event: null });
      });
  }, [eventId]);

  useEffect(load, [load]);

  const { event } = state;
  const registration = registrations.byEventId[eventId];
  const isRegistered = Boolean(registration);

  async function register() {
    setBusy(true);
    try {
      const { registration: created } = await eventsApi.registerForEvent(eventId);
      registrations.setRegistration(eventId, created);
      toast.success(`Registered. Your check-in code is ${created.checkInCode}.`);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    const ok = await confirm({
      title: 'Cancel registration?',
      message: `You'll lose your spot at "${event.name}". You can register again later if space allows.`,
      confirmLabel: 'Cancel registration',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await eventsApi.cancelRegistration(eventId);
      registrations.setRegistration(eventId, null);
      toast.success('Registration cancelled.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function viewBadge() {
    if (!registration) return;
    setBadgeBusy(true);
    try {
      const { badge } = await eventsApi.getBadge(eventId, registration.registrationId);
      openBadgeOrWarn(badge.html, toast);
    } catch (err) {
      toast.error(err);
    } finally {
      setBadgeBusy(false);
    }
  }

  async function saveEdit(payload) {
    const { event: updated } = await eventsApi.updateEvent(eventId, payload);
    setState(current => ({ ...current, event: updated }));
    toast.success('Event updated.');
    setEditing(false);
  }

  async function quickSetStatus(nextStatus) {
    if (nextStatus === 'cancelled') {
      const ok = await confirm({
        title: 'Cancel event?',
        message: `"${event.name}" will be marked cancelled and closed to new registrations. Existing registrations are unaffected — cancel them individually if needed.`,
        confirmLabel: 'Cancel event',
        danger: true,
      });
      if (!ok) return;
    }
    setStatusBusy(true);
    try {
      const { event: updated } = await eventsApi.updateEvent(eventId, { status: nextStatus });
      setState(current => ({ ...current, event: updated }));
      toast.success(`Event marked ${nextStatus}.`);
    } catch (err) {
      toast.error(err);
    } finally {
      setStatusBusy(false);
    }
  }

  return (
    <>
      <p>
        <Link to="/events">← Member events</Link>
      </p>

      {state.notFound ? (
        <EmptyState>Event not found.</EmptyState>
      ) : (
        <>
          <ErrorAlert error={state.error} />
          {state.loading ? (
            <Spinner />
          ) : !event ? null : (
            <>
              <div className={cn(ui.row, ui.spread, 'mb-4')}>
                <h1 className="text-title-l">{event.name}</h1>
                <div className={ui.row}>
                  {event.status && <StatusPill status={event.status} />}
                  {event.accreditedOnly && <span className={ui.pillInfo}>Accredited only</span>}
                </div>
              </div>

              {event.description && <p>{event.description}</p>}

              <section className={cn(ui.card, 'mb-4')}>
                <p>
                  <strong>When:</strong> {formatDateTime(event.startsAt)}
                  {event.endsAt && <> – {formatDateTime(event.endsAt)}</>}
                </p>
                <p>
                  <strong>Venue:</strong> {event.venue || '—'}
                </p>
                <p>
                  <strong>Capacity:</strong> {event.capacity == null ? 'Unlimited' : event.capacity}
                </p>
              </section>

              {isEventStaff && (
                <section className={cn(ui.card, 'mb-4')}>
                  <h3 className={ui.h3}>Staff controls</h3>
                  <div className={ui.row}>
                    <button
                      type="button"
                      className={ui.btnSecondary}
                      onClick={() => setEditing(true)}
                    >
                      Edit event
                    </button>
                    {event.status === 'draft' && (
                      <button
                        type="button"
                        className={ui.btnPrimary}
                        disabled={statusBusy}
                        onClick={() => quickSetStatus('published')}
                      >
                        {statusBusy ? 'Working…' : 'Publish'}
                      </button>
                    )}
                    {['draft', 'published'].includes(event.status) && (
                      <button
                        type="button"
                        className={ui.btnDanger}
                        disabled={statusBusy}
                        onClick={() => quickSetStatus('cancelled')}
                      >
                        {statusBusy ? 'Working…' : 'Cancel event'}
                      </button>
                    )}
                  </div>
                </section>
              )}

              <section className={cn(ui.card, 'mb-4')}>
                <h3 className={ui.h3}>Your registration</h3>
                {registrations.loading ? (
                  <Spinner />
                ) : isRegistered ? (
                  <>
                    <p>
                      <span className={ui.pillSuccess}>Registered</span>
                    </p>
                    <p>
                      <CheckInCode code={registration.checkInCode} />
                    </p>
                    <div className={ui.row}>
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        disabled={busy}
                        onClick={cancel}
                      >
                        {busy ? 'Working…' : 'Cancel registration'}
                      </button>
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        disabled={badgeBusy}
                        onClick={viewBadge}
                      >
                        {badgeBusy ? 'Opening…' : 'View my badge'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className={ui.muted}>You&rsquo;re not registered for this event.</p>
                    <button
                      type="button"
                      className={ui.btnPrimary}
                      disabled={busy}
                      onClick={register}
                    >
                      {busy ? 'Working…' : 'Register'}
                    </button>
                  </>
                )}
              </section>

              {isEventStaff && (
                <EventManage eventId={eventId} eventName={event.name} event={event} />
              )}

              <Modal open={editing} onClose={() => setEditing(false)} title="Edit event">
                <EventForm
                  initial={eventToForm(event)}
                  submitLabel="Save changes"
                  onSubmit={saveEdit}
                  onCancel={() => setEditing(false)}
                />
              </Modal>
            </>
          )}
        </>
      )}
    </>
  );
}

/**
 * Staff attendee management: paginated roster with per-row check-in/badge
 * actions, a door check-in-by-code box, and a print-all-badges action.
 */
/**
 * Virtual and hybrid events (docs/Decisions.md D55): upload a meeting's
 * attendee report (CSV) or paste addresses, and every registered member on it
 * is checked in. Unregistered addresses are listed back for follow-up.
 */
function AttendeeListUpload({ eventId, onRecorded }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const emails = parseAttendeeEmails(text);

  async function onFile(file) {
    if (!file) return;
    setText(await file.text());
    setResult(null);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!emails.length) return;
    setBusy(true);
    setError(null);
    try {
      const data = await eventsApi.recordAttendance(eventId, emails);
      setResult(data);
      onRecorded();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className={cn(ui.cardFlat, 'my-4')}
      aria-labelledby="attendance-title"
    >
      <h4 id="attendance-title" className="text-body-strong">
        Confirm attendance from an attendee list
      </h4>
      <p className={cn(ui.hint, 'mb-2')}>
        Upload the meeting&apos;s attendee report (CSV) or paste email addresses. Registered members
        on the list are checked in; it counts toward their event ribbons.
      </p>
      <ErrorAlert error={error} />
      <Field
        label="Attendee report (CSV)"
        type="file"
        accept=".csv,text/csv,text/plain"
        onChange={e => onFile(e.target.files && e.target.files[0])}
      />
      <TextareaField
        label="Or paste addresses"
        rows={3}
        value={text}
        hint={`${emails.length} address${emails.length === 1 ? '' : 'es'} found${emails.length > MAX_ATTENDANCE_EMAILS ? `; at most ${MAX_ATTENDANCE_EMAILS} per upload` : ''}.`}
        onChange={e => {
          setText(e.target.value);
          setResult(null);
        }}
      />
      <button
        type="submit"
        className={ui.btnPrimary}
        disabled={busy || !emails.length || emails.length > MAX_ATTENDANCE_EMAILS}
      >
        {busy ? 'Recording…' : 'Confirm attendance'}
      </button>
      {result ? (
        <div className={cn(ui.alertSuccess, 'mt-4')} role="status">
          <p className="m-0">
            {result.confirmed.length} checked in, {result.alreadyCheckedIn.length} already checked
            in, {result.notRegistered.length} not registered for this event.
          </p>
          {result.notRegistered.length ? (
            <p className="mt-2 mb-0 break-all">Not registered: {result.notRegistered.join(', ')}</p>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

function EventManage({ eventId, eventName, event }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [rowBusyId, setRowBusyId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);

  const list = usePagedList(
    cursor =>
      eventsApi
        .listRegistrations(eventId, { limit: ROSTER_PAGE_SIZE, lastEvaluatedKey: cursor })
        .then(d => ({ items: d.registrations || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [eventId]
  );

  async function checkInRow(reg) {
    setRowBusyId(reg.registrationId);
    try {
      const { registration: updated } = await eventsApi.checkIn(eventId, {
        registrationId: reg.registrationId,
      });
      list.mutateItem(reg.registrationId, updated, 'registrationId');
      toast.success(`${updated.attendeeName} checked in.`);
    } catch (err) {
      toast.error(err);
    } finally {
      setRowBusyId(null);
    }
  }

  async function printRow(reg) {
    setRowBusyId(reg.registrationId);
    try {
      const { badge } = await eventsApi.getBadge(eventId, reg.registrationId);
      const win = openBadgeOrWarn(badge.html, toast, { autoPrint: true });
      if (win) {
        // The badge endpoint stamps badgePrintedAt server-side but doesn't
        // return the updated registration — patch the cached row locally
        // rather than refetching the whole page for one field.
        list.mutateItem(
          reg.registrationId,
          { badgePrintedAt: new Date().toISOString() },
          'registrationId'
        );
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setRowBusyId(null);
    }
  }

  async function printAll() {
    const ok = await confirm({
      title: 'Print all badges?',
      message: `This renders and marks badgePrintedAt for every non-cancelled registration on "${eventName}", then opens one print window for the whole roster.`,
      confirmLabel: 'Print all badges',
    });
    if (!ok) return;
    setBatchBusy(true);
    try {
      const { badges } = await eventsApi.printBatch(eventId);
      const { html, rendered, failed } = buildBadgeBatchDocument(badges);
      if (!html) {
        toast.error('No badges could be rendered for printing.');
        return;
      }
      const win = openBadgeOrWarn(html, toast, { autoPrint: true });
      if (win) {
        toast.success(
          failed > 0
            ? `Opened ${rendered} badge(s) for printing — ${failed} could not be rendered.`
            : `Opened ${rendered} badge(s) for printing.`
        );
        list.reload();
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setBatchBusy(false);
    }
  }

  return (
    <section className={cn(ui.card, 'mb-4')}>
      <div className={cn(ui.row, ui.spread, 'items-baseline!')}>
        <h3 className="text-title-s">Manage attendees</h3>
        <button type="button" className={ui.btnSecondary} disabled={batchBusy} onClick={printAll}>
          {batchBusy ? 'Rendering…' : 'Print all badges'}
        </button>
      </div>

      {event?.format !== 'virtual' ? (
        <DoorCheckIn
          eventId={eventId}
          onCheckedIn={updated =>
            list.mutateItem(updated.registrationId, updated, 'registrationId')
          }
        />
      ) : null}
      {acceptsAttendeeList(event) ? (
        <AttendeeListUpload eventId={eventId} onRecorded={list.reload} />
      ) : null}

      <h4 className="my-4 text-body-strong">Roster</h4>
      <ErrorAlert error={list.error} />
      {list.loading ? (
        <Spinner />
      ) : list.items.length === 0 ? (
        !list.error && <EmptyState>No registrations yet.</EmptyState>
      ) : (
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Attendee</th>
                <th>Company</th>
                <th>Status</th>
                <th>Checked in</th>
                <th>Badge printed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.items.map(reg => {
                const rowBusy = rowBusyId === reg.registrationId;
                return (
                  <tr key={reg.registrationId}>
                    <td>
                      <UserChip userId={reg.userId} name={reg.attendeeName} />
                    </td>
                    <td>{reg.attendeeCompany || '—'}</td>
                    <td>
                      <StatusPill status={reg.status} />
                    </td>
                    <td>
                      <RelativeTime iso={reg.checkedInAt} />
                    </td>
                    <td>
                      <RelativeTime iso={reg.badgePrintedAt} />
                    </td>
                    <td className={ui.row}>
                      {reg.status === 'registered' && (
                        <button
                          type="button"
                          className={ui.btnPrimary}
                          disabled={rowBusy}
                          onClick={() => checkInRow(reg)}
                        >
                          {rowBusy
                            ? 'Working…'
                            : event?.format === 'virtual'
                              ? 'Confirm attendance'
                              : 'Check in'}
                        </button>
                      )}
                      {reg.status !== 'cancelled' && (
                        <button
                          type="button"
                          className={ui.btnSecondary}
                          disabled={rowBusy}
                          onClick={() => printRow(reg)}
                        >
                          {rowBusy ? 'Working…' : 'Print badge'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PagedListFooter
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        endLabel={list.items.length ? 'No more registrations.' : null}
      />
    </section>
  );
}

/**
 * Door check-in-by-code box: door staff scan/type the attendee's badge code
 * (never the registrationId) and press Enter. Ergonomics matter here more
 * than anywhere else on the page — autofocus, Enter submits, the field
 * clears on success (ready for the next attendee) but keeps the typed code
 * on error so staff can see/correct what was scanned.
 */
function DoorCheckIn({ eventId, onCheckedIn }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastCheckedIn, setLastCheckedIn] = useState(null);

  async function submit(e) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { registration } = await eventsApi.checkIn(eventId, { checkInCode: trimmed });
      onCheckedIn(registration);
      setLastCheckedIn(registration.attendeeName);
      toast.success(`${registration.attendeeName} checked in.`);
      setCode('');
    } catch (err) {
      setError(err);
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={cn(ui.card, 'mt-4')}>
      <h4 className="mb-4 text-body-strong">Door check-in</h4>
      <p className={cn(ui.muted, 'text-body-s')}>
        Scan or type the attendee&rsquo;s check-in code, then press Enter.
      </p>
      <ErrorAlert error={error} />
      {lastCheckedIn && !error && (
        <p className="text-body-s">
          <span className={ui.pillSuccess}>Checked in</span> {lastCheckedIn}
        </p>
      )}
      <form onSubmit={submit} className={ui.row}>
        <input
          className={ui.input}
          aria-label="Check-in code"
          autoFocus
          autoComplete="off"
          placeholder="Check-in code"
          value={code}
          disabled={busy}
          onChange={e => setCode(e.target.value)}
        />
        <button type="submit" className={ui.btnPrimary} disabled={busy || !code.trim()}>
          {busy ? 'Checking in…' : 'Check in'}
        </button>
      </form>
    </section>
  );
}
